import _ from "lodash";
/*--external-- */
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

/**--internal-- */
import { graphModes } from "Worksheet/modules/WorksheetConstants";

/**--relative--*/
import DragAndDrop from "./DragAndDrop";
import classes from "./DragAndDropField.scss";
import {
  convertToPercentage,
  convertToPixel,
  extractCorrectElementsFromData,
  injectCorrectElementsInData,
} from "./DragAndDrop/DragAndDropUtils";

/**
 *
 * @param { mode, t }
 * @returns DragAndDropField Header
 */
const DragAndDropFieldHeader = ({ mode, t }) => {
  if (mode !== "create") return null;

  return (
    <div className={classes.headerContainer}>
      <div className={classes.leftContainer}>
        <div className="text-label-2">{t("worksheet:canvas")}</div>
      </div>
    </div>
  );
};

/**
 *
 * @param {*} props
 * @returns Drag and Drop Question Component
 */
const DragAndDropField = props => {
  const {
    t = () => "",
    mode = "preview",
    fieldId = "",
    questionId = "",
    resourceId = "",
    questionIndex = 0,
    customRef = () => {},
    isSubmitted = false,
    isSubQuestion = false,
    fieldKey = "dragAndDrop",
    allowInteraction = false,
    subQuestionParentId = "",
    updateInputField = () => {},
    showViewSolutionButton = false,
    worksheetMode,
    isFromInternalTool = false,
  } = props;
  const componentRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [questionData, setQuestionData] = useState([]);
  const [canvasDimension, setCanvasDimension] = useState({});

  // * Config data for Drag and drop question
  const config = {
    fieldId,
    fieldKey,
    questionId,
    resourceId,
    isSubmitted,
    questionIndex,
    isSubQuestion,
    allowInteraction,
    subQuestionParentId,
    showViewSolutionButton,
  };

  // * Observing canvas resize in width/height and sending callback
  useEffect(() => {
    let resizeObserver;
    const observeCanvasResize = () => {
      const canvasId = `konva-canvas-container-id-${fieldId}-${mode}`;
      const targetElement = document.getElementById(canvasId);
      if (!targetElement) return;

      resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          const { width, height } = entry.contentRect;
          setCanvasDimension({ width, height });
        }
      });

      resizeObserver.observe(targetElement);
    };

    // * adding resize observer and disconnect cleanup of observer
    const observerTimeout = setTimeout(observeCanvasResize, 100);
    return () => {
      clearTimeout(observerTimeout);
      if (resizeObserver) resizeObserver.disconnect();
      resizeObserver = null;
    };
  }, [mode, fieldId, JSON.stringify(canvasDimension)]);

  // * Converting percentage data into pixel according to canvas width
  useEffect(() => {
    try {
      const elements = injectCorrectElementsInData(_.get(props, "value", {}));
      convertToPixel(elements, fieldId, mode, setQuestionData);
    } catch (error) {}
  }, [
    _.get(props, "value.elements", {}),
    isFullscreen,
    fieldId,
    mode,
    canvasDimension,
  ]);

  // * Updating local data and saving on server
  const handleDataUpdate = (
    updatedData,
    answersMapping = {},
    updateOnlyAnswer = false
  ) => {
    try {
      if (!updateOnlyAnswer) setQuestionData(updatedData);

      if (!_.includes(["create", "edit"], mode)) return;

      let params = null;

      if (updateOnlyAnswer) {
        // * Updating answer with re-drawing canvas
        const { dragAndDropAnswers } = extractCorrectElementsFromData(
          mode,
          updatedData
        );
        params = { dragAndDropAnswers };
      } else {
        // * convert to percentage, sanatize data and send to server
        const dataInPercentage = convertToPercentage(
          updatedData,
          fieldId,
          mode
        );
        if (!dataInPercentage) return;

        const { dragAndDropAnswers, elements } = extractCorrectElementsFromData(
          mode,
          dataInPercentage
        );

        params =
          mode === "create"
            ? { value: { elements }, dragAndDropAnswers }
            : { value: { elements, answersMapping } };
      }

      updateInputField?.({
        params,
        isMutation: true,
        isDebounce: true,
      });
    } catch (error) {}
  };

  // * Setting custom ref for worksheet wrapper
  useEffect(() => {
    customRef(componentRef.current);
  }, [customRef]);

  return (
    <div ref={componentRef} className={classes.container}>
      <DragAndDropFieldHeader t={t} mode={mode} />
      <DragAndDrop
        t={t}
        mode={mode}
        key={fieldId}
        config={config}
        data={questionData}
        isFullscreen={isFullscreen}
        onUpdateData={handleDataUpdate}
        onToggleFullscreen={() => setIsFullscreen(prev => !prev)}
        answersMapping={_.get(props, "value.answersMapping", {})}
        worksheetMode={worksheetMode}
        isFromInternalTool={isFromInternalTool}
      />
    </div>
  );
};

export default DragAndDropField;

DragAndDropField.displayName = "DragAndDropField";

DragAndDropField.propTypes = {
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  mode: PropTypes.oneOf(graphModes),
  updateInputField: PropTypes.func,
  fieldKey: PropTypes.string,
  questionIndex: PropTypes.number,
  isSubQuestion: PropTypes.bool,
  resourceId: PropTypes.string,
  questionId: PropTypes.string,
  fieldId: PropTypes.string,
  subQuestionParentId: PropTypes.string,
  isSubmitted: PropTypes.bool,
  allowInteraction: PropTypes.bool,
  showViewSolutionButton: PropTypes.bool,
};
